<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import { useAlert } from 'dashboard/composables';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';

const MAX_DAYS = 30;
const MAX_SECONDS = MAX_DAYS * 24 * 60 * 60;

const { t } = useI18n();
const store = useStore();

const isDownloading = ref(false);
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

const conversationDetails = computed(
  () => store.getters.getConversationDetails ?? []
);
const uiFlags = computed(() => store.getters.getConversationDetailsUIFlags);
const isLoading = computed(() => uiFlags.value?.isFetching ?? false);

const from = computed(() => {
  return getUnixStartOfDay(customDateRange.value[0]);
});

const to = computed(() => {
  return getUnixEndOfDay(customDateRange.value[1]);
});

const isDateRangeValid = computed(() => {
  const since = from.value;
  const until = to.value;
  if (!since || !until || until <= since) return false;
  return until - since <= MAX_SECONDS;
});

const dateRangeError = computed(() => {
  const since = from.value;
  const until = to.value;
  if (!since || !until || until <= since) return '';
  if (until - since > MAX_SECONDS) {
    return t('REPORT.CONVERSATION_DETAILS.DATE_RANGE_TOO_LONG', {
      max_days: MAX_DAYS,
    });
  }
  return '';
});

const columnHelper = createColumnHelper();
const columns = computed(() => [
  columnHelper.accessor('conversation_id', {
    header: t('REPORT.CONVERSATION_DETAILS.CONVERSATION_ID'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 100,
  }),
  columnHelper.accessor('contact_id', {
    header: t('REPORT.CONVERSATION_DETAILS.CONTACT_ID'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 100,
  }),
  columnHelper.accessor('assignee', {
    header: t('REPORT.CONVERSATION_DETAILS.ASSIGNEE'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 120,
  }),
  columnHelper.accessor('channel', {
    header: t('REPORT.CONVERSATION_DETAILS.CHANNEL'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 120,
  }),
  columnHelper.accessor('message_created', {
    header: t('REPORT.CONVERSATION_DETAILS.MESSAGE_CREATED'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 160,
  }),
  columnHelper.accessor('ticket_created', {
    header: t('REPORT.CONVERSATION_DETAILS.TICKET_CREATED'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 160,
  }),
  columnHelper.accessor('waiting_time', {
    header: t('REPORT.CONVERSATION_DETAILS.WAITING_TIME'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 100,
  }),
  columnHelper.accessor('first_response_time_date', {
    header: t('REPORT.CONVERSATION_DETAILS.FIRST_RESPONSE_TIME_DATE'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 160,
  }),
  columnHelper.accessor('first_response_time_duration', {
    header: t('REPORT.CONVERSATION_DETAILS.FIRST_RESPONSE_TIME_DURATION'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 120,
  }),
  columnHelper.accessor('resolution_time', {
    header: t('REPORT.CONVERSATION_DETAILS.RESOLUTION_TIME'),
    cell: ctx => ctx.getValue() ?? '—',
    size: 100,
  }),
]);

const table = useVueTable({
  get data() {
    return conversationDetails.value ?? [];
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: { pageSize: 10 },
  },
});

function fetchData() {
  if (!isDateRangeValid.value) return;
  const promise = store.dispatch('fetchConversationDetails', {
    from: from.value,
    to: to.value,
  });
  if (promise && typeof promise.catch === 'function') {
    promise.catch(err => {
      if (err?.message) useAlert()(err.message);
    });
  }
}

function downloadCSV() {
  if (!isDateRangeValid.value) {
    useAlert()(
      t('REPORT.CONVERSATION_DETAILS.DATE_RANGE_TOO_LONG', {
        max_days: MAX_DAYS,
      })
    );
    return;
  }
  isDownloading.value = true;
  const fileName = generateFileName({
    type: 'conversation_details',
    to: to.value,
  });
  const promise = store.dispatch('downloadConversationDetailsCSV', {
    from: from.value,
    to: to.value,
    fileName,
  });
  if (promise && typeof promise.finally === 'function') {
    promise
      .catch(err => {
        const msg =
          err?.message ||
          err?.response?.data?.message ||
          t('REPORT.CONVERSATION_DETAILS.DOWNLOAD_FAILED');
        useAlert()(msg);
      })
      .finally(() => {
        isDownloading.value = false;
      });
  } else {
    isDownloading.value = false;
  }
}

function onDateRangeChange(value) {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
}

onMounted(() => {
  fetchData();
});

watch(
  () => [from.value, to.value],
  () => {
    if (isDateRangeValid.value) {
      fetchData();
    }
  },
  { immediate: false }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col justify-between gap-3 md:flex-row">
      <div
        class="w-full grid gap-y-2 gap-x-1.5 grid-cols-[repeat(auto-fill,minmax(250px,1fr))]"
      >
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
      </div>
      <div class="flex items-center gap-3">
        <Button
          size="sm"
          icon="i-ph-download-simple"
          :label="
            isDownloading
              ? $t('REPORT.CONVERSATION_DETAILS.DOWNLOADING')
              : $t('REPORT.CONVERSATION_DETAILS.DOWNLOAD_CSV')
          "
          :disabled="!isDateRangeValid || isDownloading"
          :is-loading="isDownloading"
          @click="downloadCSV"
        />
      </div>
    </div>

    <p v-if="dateRangeError" class="text-sm text-red-600 dark:text-red-400">
      {{ dateRangeError }}
    </p>

    <div
      class="flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <div v-if="isLoading" class="flex items-center justify-center p-8">
        <Spinner />
        <span class="ml-2">{{
          $t('REPORT.CONVERSATION_DETAILS.LOADING')
        }}</span>
      </div>
      <template v-else>
        <div class="overflow-x-auto">
          <Table :table="table" />
          <Pagination
            v-if="conversationDetails.length"
            class="mt-2"
            :table="table"
            show-page-size-selector
            :default-page-size="10"
          />
          <EmptyState
            v-else
            :title="$t('REPORT.CONVERSATION_DETAILS.NO_DATA')"
          />
        </div>
      </template>
    </div>
  </div>
</template>
